import { Flex, type FlexProps, Text } from "@mantine/core";
import { Badge } from "@mantine/core";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Select } from "src/components/shared/MantineCombobox";
import { useIsExpired } from "src/generator/hooks/useIsExpired";
import { useOpenLogbookEntry } from "src/generator/hooks/useOpenLogbookEntry";
import {
  getLogbookIdType,
  navigateToLogbookEntryAfterPrefetch,
  resolveLatestEntryIdForLogbook,
} from "src/generator/utils/navigateToLogbookEntry";
import { useIsNewEntry, useIsOnline } from "src/hooks";
import { useGetApi } from "src/hooks";
import { mapSummary } from "src/pages/Summary/Summary.utils";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setReleaseEntryLock } from "src/redux/slices/entrySlice";
import { setInitiallyOpen } from "src/redux/slices/generatorSlice";
import { parseErrorMessage } from "src/utils";
import { groupComboboxItems, MEDLBID } from "src/utils";
import { ENTRY_STATUS, isUnverifiedStatus } from "src/utils/entryStatus";
import { mantineRenderOption } from "src/utils/mantineRenderOption";
import { Notification } from "src/utils/Notification";

import { EntrySelectItem } from "../EntrySelectItem/EntrySelectItem";
import {
  logBarSelectClassNames,
  logBarSelectComboboxProps,
  logBarSelectStyles,
} from "../logBarSelectStyles";

const LogBarSelects = (props: FlexProps) => {
  const entriesRef = useRef<HTMLInputElement>(null);
  const isNewEntry = useIsNewEntry();
  const isOnline = useIsOnline();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const openEntry = useOpenLogbookEntry();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const isExpiredLogbook = useIsExpired();
  const dispatch = useDispatch();
  const { entries, logbooks, latestEntry, collectionId } = useSelector(
    state => state.generatorSlice,
  );
  const allPendingNewEntries = useSelector(
    state => state.pendingRequestsSlice.pendingNewEntries,
  );

  const [reviewData, setReviewData] = useState<Array<any>>();
  const sophiaLbId = "aebb3679-9132-11ef-8b69-02c1fe2db5a5";
  const showVerifyCount = logbookId === sophiaLbId;

  const apiArgs = useMemo(
    () => ({
      request: "get-review",
      body: {
        params: { req: "all" },
        _logbook: logbookId,
        _status: "*",
      },
      mapper: mapSummary,
      fetchOnInit: false,
    }),
    [logbookId],
  );

  const { data: verifyData, refetch: fetchVerify } = useGetApi(apiArgs);

  useEffect(() => {
    if (!showVerifyCount) return;
    fetchVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [showVerifyCount]);

  useEffect(() => {
    if (verifyData?.reviews) {
      setReviewData(verifyData.reviews);
    } else {
      setReviewData(undefined);
    }
  }, [verifyData]);

  const pendingEntries = useMemo(
    () =>
      allPendingNewEntries
        .filter(p => p.lbid === logbookId)
        .map(p => ({
          group: "Offline",
          value: p.id,
          label: dayjs(p.created).format("D MMM YYYY - HH:mm"),
          status: ENTRY_STATUS.OPEN,
        })),
    [allPendingNewEntries, logbookId],
  );
  const flatEntries = useMemo(() => {
    const onlineEntries = Array.isArray(entries) ? entries : [];
    const items =
      pendingEntries.length > 0
        ? [
            ...pendingEntries,
            ...onlineEntries.map((e: any) => ({ ...e, group: "Online" })),
          ]
        : onlineEntries;

    return items.map((e: any) => ({
      ...e,
      label: `${String(e.label ?? "").split(" - ")[0]}${
        e.patient && String(e.patient).trim()
          ? " - " + String(e.patient).trim()
          : ""
      }`,
    }));
  }, [entries, pendingEntries]);

  const entriesSelectData = useMemo(
    () => groupComboboxItems(flatEntries),
    [flatEntries],
  );

  const handleSelectChange = useCallback(
    async (value: string) => {
      if (!value || !logbookId || !idType) return;
      if (value === String(latestEntry.id ?? lbeidOrPartId)) return;

      dispatch(setInitiallyOpen(false));
      dispatch(setReleaseEntryLock(Math.random()));

      if (entriesRef.current) entriesRef.current.blur();

      if (!isOnline && window?.api?.get) {
        const ok = await navigateToLogbookEntryAfterPrefetch(
          navigate,
          setSearchParams,
          logbookId,
          idType,
          value,
        );
        if (!ok) {
          Notification(
            "Error",
            parseErrorMessage(
              "Unable to load data as you appear to be offline. get-logbook",
            ),
            "red",
          );
        }
        return;
      }

      openEntry(value);
    },
    [
      dispatch,
      isOnline,
      latestEntry.id,
      lbeidOrPartId,
      logbookId,
      navigate,
      idType,
      openEntry,
      setSearchParams,
    ],
  );

  const handleLogbookChange = useCallback(
    async (id: string | null) => {
      dispatch(setReleaseEntryLock(Math.random()));

      const lb = logbooks.find(logbook => logbook.value === id);
      if (!lb) return;

      if (lb.lbtype === "collection") {
        navigate(`/collection/${lb.value}`);
        return;
      }

      if (!isOnline && window?.api?.get) {
        const idType = getLogbookIdType(lb);
        const entryId = await resolveLatestEntryIdForLogbook(
          lb.value,
          lb.lbtype,
          lb.latest,
        );
        if (!entryId) {
          Notification(
            "Error",
            parseErrorMessage(
              "Unable to load data as you appear to be offline. get-logbook",
            ),
            "red",
          );
          return;
        }
        const ok = await navigateToLogbookEntryAfterPrefetch(
          navigate,
          setSearchParams,
          lb.value,
          idType,
          entryId,
        );
        if (!ok) {
          Notification(
            "Error",
            parseErrorMessage(
              "Unable to load data as you appear to be offline. get-logbook",
            ),
            "red",
          );
        }
        return;
      }

      if (lb.lbtype === "part") {
        navigate(`/logbook/${lb.value}/partid/?lbTypeStatus=null`);
      } else if (lb.lbtype === "logbook") {
        if (lb.value === MEDLBID) {
          navigate(`/logbook/${lb.value}/meid`);
        } else {
          navigate(`/logbook/${lb.value}/lbeid`);
        }
      }
    },
    [dispatch, isOnline, logbooks, navigate, setSearchParams],
  );

  return (
    <Flex
      gap={20}
      justify="space-between"
      align="center"
      wrap="wrap"
      {...props}>
      {showVerifyCount && (
        <Flex direction="row" gap={4}>
          <Badge variant="light" size="lg" color={"orange.4"} py={10} miw={80}>
            <Flex gap={6} align="center">
              Requires Verification
              <Text size="sm" c={"orange.8"}>
                {
                  reviewData?.filter(
                    e =>
                      [
                        ENTRY_STATUS.OPEN,
                        ENTRY_STATUS.REVERIFY,
                        ENTRY_STATUS.PENDING,
                      ].includes(e.status) &&
                      !e.primaryValid &&
                      (e.lbentry.sign === "both" ||
                        isUnverifiedStatus(e.status)),
                  ).length
                }
              </Text>
            </Flex>
          </Badge>
          <Badge variant="light" size="lg" color={"orange.4"} py={10} miw={80}>
            <Flex gap={6} align="center">
              Requires Co-Sign
              <Text size="sm" c={"orange.8"}>
                {
                  reviewData?.filter(
                    e =>
                      [
                        ENTRY_STATUS.OPEN,
                        ENTRY_STATUS.REVERIFY,
                        ENTRY_STATUS.PENDING,
                      ].includes(e.status) &&
                      !e.secondaryValid &&
                      (e.lbentry.sign === "both" ||
                        (e.lbentry.sign && e.lbentry.sign === "both")),
                  ).length
                }
              </Text>
            </Flex>
          </Badge>
        </Flex>
      )}
      <Flex
        style={{ flex: 1, justifyContent: "center" }}
        gap={20}
        align="center"
        wrap="wrap">
        <Flex gap={15} align="center">
          <Text>Entries</Text>
          <Select
            name="entry"
            ref={entriesRef}
            searchable
            limit={Infinity}
            miw={idType === "meid" ? "400px" : "315px"}
            placeholder={
              entries?.length !== 0 ? "Choose an entry" : "No Entries"
            }
            data={entriesSelectData}
            renderOption={mantineRenderOption(EntrySelectItem)}
            styles={logBarSelectStyles}
            classNames={logBarSelectClassNames}
            comboboxProps={{
              ...logBarSelectComboboxProps,
              maxDropdownHeight: 300,
            }}
            value={
              isNewEntry
                ? null
                : latestEntry.id
                  ? String(latestEntry.id)
                  : lbeidOrPartId
                    ? String(lbeidOrPartId)
                    : null
            }
            onChange={value => value !== null && handleSelectChange(value)}
          />
        </Flex>
        {!isExpiredLogbook && (
          <Flex gap={15} align="center">
            <Text>Logbooks</Text>
            <Select
              data-cy="logbook-select"
              searchable
              miw={"315px"}
              placeholder="Pick one"
              styles={logBarSelectStyles}
              classNames={logBarSelectClassNames}
              comboboxProps={logBarSelectComboboxProps}
              value={
                collectionId || logbookId
                  ? String(collectionId || logbookId)
                  : null
              }
              onChange={handleLogbookChange}
              data={Array.isArray(logbooks) ? logbooks : []}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(LogBarSelects);
